import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import gtmService, { Icp, Persona, Offer } from '../services/gtmService';
import icpCampaignService from '../services/icpCampaignService';
import { strategyApi, StrategyResult } from '../services/api';
import { PageHeader, useConfirm, useFocusParam } from '../components/ui';
import { routeById } from '../navigation/routes';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { RechteHinweis } from '../vertrieb/RechteHinweis';

type Tab = 'icp' | 'persona' | 'offer';

// Bezeichnung je Reiter fuer Dialogtitel.
const TAB_LABEL: Record<Tab, string> = { icp: 'ICP', persona: 'Persona', offer: 'Angebot' };

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  archived: 'Archiviert',
};

/*
 * Welle 2: Generieren, Status, Loeschen, Strategie-Uebernahme und
 * "ICP-Kampagne starten" sind Schreibaufrufe (vertrieb.bearbeiten). Die
 * Strategien (GET /strategy, LinkedIn-Router) laedt die Seite nur, wenn sie
 * uebernommen werden duerfen und linkedin.lesen vorliegt.
 */
const GtmSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const siehtStrategien = darfBearbeiten && darf('linkedin.lesen');
  // ?tab=icp|persona|offer&id=<Eintrag>: Sprung aus ICP-/Signal-Kampagnen.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const t = searchParams.get('tab');
    return t === 'persona' || t === 'offer' ? t : 'icp';
  });
  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(() => searchParams.get('id'));
  const [strategies, setStrategies] = useState<StrategyResult[]>([]);
  const [strategyId, setStrategyId] = useState('');

  // generation forms
  const [icpForm, setIcpForm] = useState({ product: '', existingCustomers: '', pricing: '', constraints: '' });
  const [personaForm, setPersonaForm] = useState({ icpId: '', product: '', context: '' });
  const [offerForm, setOfferForm] = useState({ product: '', icpId: '', features: '', context: '' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [icpData, personaData, offerData] = await Promise.all([
        gtmService.getIcps(),
        gtmService.getPersonas(),
        gtmService.getOffers(),
      ]);
      setIcps(icpData);
      setPersonas(personaData);
      setOffers(offerData);
      if (siehtStrategien) {
        strategyApi.getAll().then(setStrategies).catch(() => {
          /* Strategy import is optional. */
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der GTM-Daten');
    } finally {
      setLoading(false);
    }
  }, [siehtStrategien]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useFocusParam('id', !loading);

  const flash = (msg: string) => {
    setInfo(msg);
    setError('');
    setTimeout(() => setInfo(''), 4000);
  };

  // --- generation handlers ---
  const handleGenerateIcp = async () => {
    if (!icpForm.product.trim()) {
      setError('Bitte Produkt (URL oder Beschreibung) angeben');
      return;
    }
    try {
      setGenerating(true);
      setError('');
      const { icp } = await gtmService.generateIcp(icpForm);
      setIcps((prev) => [icp, ...prev]);
      setExpandedId(icp.id);
      setIcpForm({ product: '', existingCustomers: '', pricing: '', constraints: '' });
      flash(`ICP "${icp.name}" generiert.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Generieren des ICP');
    } finally {
      setGenerating(false);
    }
  };

  const handleImportStrategy = async () => {
    if (!strategyId) {
      setError('Bitte eine Strategie wählen');
      return;
    }
    try {
      setError('');
      const { icp, alreadyLinked } = await gtmService.createIcpFromStrategy(strategyId);
      setIcps((prev) => (alreadyLinked || prev.some((x) => x.id === icp.id) ? prev : [icp, ...prev]));
      setExpandedId(icp.id);
      setStrategyId('');
      flash(alreadyLinked ? 'Strategie war bereits als ICP übernommen.' : `ICP „${icp.name}" aus Strategie erstellt.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Übernehmen der Strategie');
    }
  };

  const handleGeneratePersona = async () => {
    if (!personaForm.icpId && !personaForm.product.trim() && !personaForm.context.trim()) {
      setError('Bitte ICP wählen oder Produkt/Kontext angeben');
      return;
    }
    try {
      setGenerating(true);
      setError('');
      const { persona } = await gtmService.generatePersona({
        icpId: personaForm.icpId || undefined,
        product: personaForm.product || undefined,
        context: personaForm.context || undefined,
      });
      setPersonas((prev) => [persona, ...prev]);
      setExpandedId(persona.id);
      setPersonaForm({ icpId: '', product: '', context: '' });
      flash(`Persona "${persona.name}" generiert.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Generieren der Persona');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateOffer = async () => {
    if (!offerForm.product.trim()) {
      setError('Bitte Produkt (URL oder Beschreibung) angeben');
      return;
    }
    try {
      setGenerating(true);
      setError('');
      const { offer } = await gtmService.generateOffer({
        product: offerForm.product,
        icpId: offerForm.icpId || undefined,
        features: offerForm.features || undefined,
        context: offerForm.context || undefined,
      });
      setOffers((prev) => [offer, ...prev]);
      setExpandedId(offer.id);
      setOfferForm({ product: '', icpId: '', features: '', context: '' });
      flash(`Angebot "${offer.name}" generiert.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Generieren des Angebots');
    } finally {
      setGenerating(false);
    }
  };

  // --- delete / status handlers ---
  const handleDelete = async (type: Tab, item: { id: string; name: string }) => {
    const { id, name } = item;
    const ok = await confirm({
      title: `${TAB_LABEL[type]} „${name}“ löschen?`,
      message:
        type === 'icp'
          ? 'Das ICP wird dauerhaft gelöscht; Personas, Angebote und Kampagnen, die darauf verweisen, verlieren die Verknüpfung. Rückgängig nur durch Neuanlage.'
          : `${TAB_LABEL[type]} „${name}“ wird dauerhaft gelöscht und steht Kampagnen nicht mehr zur Auswahl. Rückgängig nur durch Neuanlage.`,
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      if (type === 'icp') {
        await gtmService.deleteIcp(id);
        setIcps((prev) => prev.filter((x) => x.id !== id));
      } else if (type === 'persona') {
        await gtmService.deletePersona(id);
        setPersonas((prev) => prev.filter((x) => x.id !== id));
      } else {
        await gtmService.deleteOffer(id);
        setOffers((prev) => prev.filter((x) => x.id !== id));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const handleStatusChange = async (type: Tab, id: string, status: string) => {
    try {
      if (type === 'icp') {
        const updated = await gtmService.updateIcp(id, { status });
        setIcps((prev) => prev.map((x) => (x.id === id ? { ...x, ...updated } : x)));
      } else if (type === 'persona') {
        const updated = await gtmService.updatePersona(id, { status });
        setPersonas((prev) => prev.map((x) => (x.id === id ? { ...x, ...updated } : x)));
      } else {
        const updated = await gtmService.updateOffer(id, { status });
        setOffers((prev) => prev.map((x) => (x.id === id ? { ...x, ...updated } : x)));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren');
    }
  };

  const field = (label: string, value?: string) =>
    value ? (
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--sec-fg-subtle)' }}>
          {label}
        </div>
        <div style={{ fontSize: '14px', color: 'var(--sec-fg)', whiteSpace: 'pre-wrap' }}>{value}</div>
      </div>
    ) : null;

  const statusSelect = (type: Tab, item: { id: string; status: string }) => (
    <select
      value={item.status}
      disabled={!darfBearbeiten}
      aria-label="Status"
      onChange={(e) => handleStatusChange(type, item.id, e.target.value)}
      className="form-input"
      style={{ width: 'auto', display: 'inline-block', padding: '4px 8px', fontSize: '12px' }}
    >
      {Object.entries(STATUS_LABELS).map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );

  // Start an ICP campaign pre-grounded on this ICP, then jump into the wizard.
  const handleStartCampaign = async (icp: { id: string; name: string }) => {
    setError('');
    try {
      const campaign = await icpCampaignService.create({
        name: `${icp.name} – Kampagne`,
        icpId: icp.id,
      });
      navigate(`/icp-campaigns/${campaign.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Anlegen der ICP-Kampagne');
    }
  };

  const renderCardHeader = (
    type: Tab,
    item: { id: string; name: string; status: string; aiGenerated: boolean }
  ) => (
    <div className="card-header">
      <div>
        <div className="card-title">
          {item.name}{' '}
          {item.aiGenerated && (
            <span style={{ fontSize: '11px', color: 'var(--sec-coral-600)', fontWeight: 600 }}>· KI</span>
          )}
        </div>
      </div>
      <div className="btn-group">
        {statusSelect(type, item)}
        {darfBearbeiten && type === 'icp' && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handleStartCampaign(item)}
            title="Eine ICP-Kampagne auf Basis dieses ICP starten (Lemlist-Suche → Anreicherung → Push)"
          >
            ICP-Kampagne starten
          </button>
        )}
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
        >
          {expandedId === item.id ? 'Einklappen' : 'Details'}
        </button>
        {darfBearbeiten && (
          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(type, item)}>
            Löschen
          </button>
        )}
      </div>
    </div>
  );

  const renderIcpTab = () => (
    <>
      {siehtStrategien && strategies.length > 0 && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-success)' }}>
          <h2 className="form-section-title">Aus Strategie übernehmen</h2>
          <p style={{ fontSize: '12px', color: 'var(--sec-fg-muted)', marginBottom: '8px' }}>
            Übernimm einen ICP aus dem LinkedIn-Strategie-Prozess („Strategie &amp; ICP"), damit beide
            nicht doppelt gepflegt werden.
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <select
              className="form-input"
              style={{ flex: '1 1 240px' }}
              value={strategyId}
              onChange={(e) => setStrategyId(e.target.value)}
            >
              <option value="">— Strategie wählen —</option>
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <button className="btn btn-secondary" onClick={handleImportStrategy}>
              Übernehmen
            </button>
          </div>
        </div>
      )}

      {darfBearbeiten && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <h2 className="form-section-title">ICP generieren (icp-definer)</h2>
          <div className="form-group">
            <label className="form-label">Produkt (URL oder 1-Satz-Beschreibung) *</label>
            <input
              className="form-input"
              value={icpForm.product}
              onChange={(e) => setIcpForm({ ...icpForm, product: e.target.value })}
              placeholder="z.B. https://… oder 'Wir helfen B2B-SaaS, Cold Outreach zu automatisieren'"
              disabled={generating}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Beste aktuelle Kunden (optional)</label>
            <input
              className="form-input"
              value={icpForm.existingCustomers}
              onChange={(e) => setIcpForm({ ...icpForm, existingCustomers: e.target.value })}
              placeholder="2-3 deiner besten Kunden heute"
              disabled={generating}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Preissignal (optional)</label>
            <input
              className="form-input"
              value={icpForm.pricing}
              onChange={(e) => setIcpForm({ ...icpForm, pricing: e.target.value })}
              placeholder="z.B. ~500 €/Monat"
              disabled={generating}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Einschränkungen (optional)</label>
            <input
              className="form-input"
              value={icpForm.constraints}
              onChange={(e) => setIcpForm({ ...icpForm, constraints: e.target.value })}
              placeholder="Geografie, Branche, Firmengröße"
              disabled={generating}
            />
          </div>
          <button className="btn btn-success" onClick={handleGenerateIcp} disabled={generating}>
            {generating ? 'Generiere…' : 'ICP generieren'}
          </button>
        </div>
      )}

      {icps.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-description">{darfBearbeiten ? 'Noch keine ICPs. Generiere deinen ersten.' : 'Noch keine ICPs.'}</div>
        </div>
      ) : (
        icps.map((icp) => (
          <div className="card" key={icp.id} data-focus-id={icp.id}>
            {renderCardHeader('icp', icp)}
            <div className="card-description">{icp.description}</div>
            {expandedId === icp.id && (
              <div style={{ marginTop: '12px' }}>
                {field('Branche', icp.industry)}
                {field('Firmengröße', icp.companySize)}
                {field('Geografie', icp.geography)}
                {field('Signale / Trigger', icp.signals)}
                {field('Pain Points', icp.painPoints)}
                {field('Begründung', icp.rationale)}
                <div className="card-footer" style={{ fontSize: '12px', color: 'var(--sec-fg-subtle)' }}>
                  {icp._count ? `${icp._count.personas} Personas · ${icp._count.offers} Angebote · ` : ''}
                  Status: {STATUS_LABELS[icp.status] || icp.status}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </>
  );

  const renderPersonaTab = () => (
    <>
      {darfBearbeiten && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <h2 className="form-section-title">Persona generieren (persona-definer)</h2>
          <div className="form-group">
            <label className="form-label">Verknüpfter ICP (optional)</label>
            <select
              className="form-input"
              value={personaForm.icpId}
              onChange={(e) => setPersonaForm({ ...personaForm, icpId: e.target.value })}
              disabled={generating}
            >
              <option value="">Kein ICP</option>
              {icps.map((icp) => (
                <option key={icp.id} value={icp.id}>
                  {icp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Produkt (optional, falls kein ICP)</label>
            <input
              className="form-input"
              value={personaForm.product}
              onChange={(e) => setPersonaForm({ ...personaForm, product: e.target.value })}
              disabled={generating}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Zusätzlicher Kontext (optional)</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={personaForm.context}
              onChange={(e) => setPersonaForm({ ...personaForm, context: e.target.value })}
              disabled={generating}
            />
          </div>
          <button className="btn btn-success" onClick={handleGeneratePersona} disabled={generating}>
            {generating ? 'Generiere…' : 'Persona generieren'}
          </button>
        </div>
      )}

      {personas.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-description">{darfBearbeiten ? 'Noch keine Personas. Generiere die erste.' : 'Noch keine Personas.'}</div>
        </div>
      ) : (
        personas.map((p) => (
          <div className="card" key={p.id} data-focus-id={p.id}>
            {renderCardHeader('persona', p)}
            <div className="card-description">
              {p.description}
              {p.icp && (
                <span style={{ color: 'var(--sec-fg-subtle)', fontSize: '12px' }}> · ICP: {p.icp.name}</span>
              )}
            </div>
            {expandedId === p.id && (
              <div style={{ marginTop: '12px' }}>
                {field('Seniorität', p.seniority)}
                {field('Buying Role', p.buyingRole)}
                {field('Verantwortlichkeiten', p.responsibilities)}
                {field('Ziele', p.goals)}
                {field('Pain Points', p.painPoints)}
                {field('Einwände', p.objections)}
              </div>
            )}
          </div>
        ))
      )}
    </>
  );

  const renderOfferTab = () => (
    <>
      {darfBearbeiten && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <h2 className="form-section-title">Angebot generieren (offer-definer)</h2>
          <div className="form-group">
            <label className="form-label">Produkt (URL oder Beschreibung) *</label>
            <input
              className="form-input"
              value={offerForm.product}
              onChange={(e) => setOfferForm({ ...offerForm, product: e.target.value })}
              disabled={generating}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Verknüpfter ICP (optional)</label>
            <select
              className="form-input"
              value={offerForm.icpId}
              onChange={(e) => setOfferForm({ ...offerForm, icpId: e.target.value })}
              disabled={generating}
            >
              <option value="">Kein ICP</option>
              {icps.map((icp) => (
                <option key={icp.id} value={icp.id}>
                  {icp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Features (optional — werden in Outcomes übersetzt)</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={offerForm.features}
              onChange={(e) => setOfferForm({ ...offerForm, features: e.target.value })}
              disabled={generating}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Zusätzlicher Kontext (optional)</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={offerForm.context}
              onChange={(e) => setOfferForm({ ...offerForm, context: e.target.value })}
              disabled={generating}
            />
          </div>
          <button className="btn btn-success" onClick={handleGenerateOffer} disabled={generating}>
            {generating ? 'Generiere…' : 'Angebot generieren'}
          </button>
        </div>
      )}

      {offers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-description">{darfBearbeiten ? 'Noch keine Angebote. Generiere das erste.' : 'Noch keine Angebote.'}</div>
        </div>
      ) : (
        offers.map((o) => (
          <div className="card" key={o.id} data-focus-id={o.id}>
            {renderCardHeader('offer', o)}
            <div className="card-description">
              {o.oneLiner}
              {o.icp && (
                <span style={{ color: 'var(--sec-fg-subtle)', fontSize: '12px' }}> · ICP: {o.icp.name}</span>
              )}
            </div>
            {expandedId === o.id && (
              <div style={{ marginTop: '12px' }}>
                {field('Value Props', o.valueProps)}
                {field('Outcomes', o.outcomes)}
                {field('Proof Points', o.proofPoints)}
              </div>
            )}
          </div>
        ))
      )}
    </>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <div className="loading-text">Lade GTM-Setup…</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader title="Zielkunden, Personas & Angebote (GTM-Setup)" subtitle={routeById('gtm-setup')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Das vorgelagerte Fundament für Outbound: definiere wiederverwendbare ICPs, Personas und Angebote
        (powered by den lemlist-Skills <em>icp-definer</em>, <em>persona-definer</em>, <em>offer-definer</em>),
        auf Zielfirmen-Recherche und Kaufsignale aufsetzen.
      </p>

      <div className="btn-group" style={{ marginBottom: '24px' }}>
        <button
          className={`btn ${tab === 'icp' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('icp')}
        >
          ICPs ({icps.length})
        </button>
        <button
          className={`btn ${tab === 'persona' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('persona')}
        >
          Personas ({personas.length})
        </button>
        <button
          className={`btn ${tab === 'offer' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('offer')}
        >
          Angebote ({offers.length})
        </button>
      </div>

      <RechteHinweis was="ICPs, Personas und Angebote erzeugen und pflegen" />

      {error && <div className="alert alert-error">{error}</div>}
      {info && <div className="alert alert-success">{info}</div>}

      {tab === 'icp' && renderIcpTab()}
      {tab === 'persona' && renderPersonaTab()}
      {tab === 'offer' && renderOfferTab()}
    </div>
  );
};

export default GtmSetupPage;
